import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Calendar, Clock, LogOut, Sparkles, Moon, MessageCircle } from "lucide-react";
import LogoType from "@/components/LogoType";
import { useAccount } from "@/context/AccountAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

export default function Account() {
  const { user, loading, login, logout } = useAccount();
  const [appts, setAppts] = useState([]);
  const [loadingAppts, setLoadingAppts] = useState(false);

  useEffect(() => {
    document.title = "My Readings | Fairy Yasha";
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoadingAppts(true);
    axios
      .get(`${API}/account/appointments`, { withCredentials: true })
      .then((r) => setAppts(r.data || []))
      .catch(() => setAppts([]))
      .finally(() => setLoadingAppts(false));
  }, [user]);

  return (
    <div className="min-h-screen bg-[#0B0605] text-[#EFE3CC]">
      <div className="grain-overlay" aria-hidden="true" />

      <header className="mx-auto max-w-[1100px] px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/"><LogoType size="sm" /></Link>
        <Link
          to="/"
          data-testid="account-back"
          className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#B7A488] hover:text-[#E9D3A0] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Home
        </Link>
      </header>

      <div className="mx-auto max-w-[1100px] px-6 lg:px-10 pt-10 pb-28" data-testid="account-page">
        {loading ? (
          <div className="flex justify-center py-32" data-testid="account-loading">
            <Loader2 className="w-6 h-6 text-[#C9A24B] animate-spin" />
          </div>
        ) : !user ? (
          <SignedOut onLogin={login} />
        ) : (
          <SignedIn
            user={user}
            appts={appts}
            loadingAppts={loadingAppts}
            onLogout={logout}
          />
        )}
      </div>
    </div>
  );
}

function SignedOut({ onLogin }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease }}
      className="flex flex-col items-center text-center py-24"
      data-testid="account-signed-out"
    >
      <Moon className="w-8 h-8 text-[#C9A24B] mb-6" strokeWidth={1.3} />
      <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A24B] mb-4">✦ My Readings ✦</span>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
        Enter your <span className="italic text-gold-gradient">sacred space</span>
      </h1>
      <p className="mt-6 text-sm sm:text-base text-[#B7A488] font-light max-w-md">
        Sign in to view and manage every reading you've booked with Fairy Yasha, all in one gentle place.
      </p>
      <button
        onClick={onLogin}
        data-testid="google-signin-button"
        className="mt-10 group flex items-center gap-3 rounded-full bg-[#EFE3CC] px-8 py-4 text-xs tracking-[0.2em] uppercase text-[#140B09] transition-all duration-300 hover:bg-white"
      >
        <GoogleGlyph />
        Continue with Google
      </button>
    </motion.div>
  );
}

function SignedIn({ user, appts, loadingAppts, onLogout }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease }}
      data-testid="account-signed-in"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-14 h-14 rounded-full border border-[#C9A24B]/40 object-cover"
              data-testid="account-avatar"
            />
          ) : (
            <div className="w-14 h-14 rounded-full border border-[#C9A24B]/40 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#C9A24B]" strokeWidth={1.4} />
            </div>
          )}
          <div>
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A24B]">✦ Welcome back ✦</span>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter leading-none mt-1" data-testid="account-name">
              {user.name || user.email}
            </h1>
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="account-logout-button"
          className="flex items-center gap-2 self-start rounded-full border border-[#C9A24B]/40 px-5 py-2.5 text-xs tracking-[0.18em] uppercase text-[#B7A488] transition-all duration-300 hover:border-[#C9A24B] hover:text-[#EFE3CC]"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} /> Sign out
        </button>
      </div>

      <h2 className="text-sm tracking-[0.28em] uppercase text-[#E9D3A0] mb-6">Your readings</h2>

      {loadingAppts ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#C9A24B] animate-spin" />
        </div>
      ) : appts.length === 0 ? (
        <div
          className="rounded-sm border border-[#C9A24B]/18 bg-[#1F130E] p-10 text-center"
          data-testid="account-empty"
        >
          <p className="text-[#B7A488] font-light">
            You don't have any readings booked yet.
          </p>
          <Link
            to="/pricing"
            data-testid="account-book-cta"
            className="mt-6 inline-block rounded-full border border-[#C9A24B]/50 px-8 py-3.5 text-xs tracking-[0.2em] uppercase text-[#EFE3CC] transition-all duration-300 hover:border-[#C9A24B] hover:bg-[#C9A24B]/10"
          >
            Book your first reading
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="account-appointments">
          {appts.map((a) => (
            <div
              key={a.id}
              data-testid="account-appointment-card"
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-sm border border-[#C9A24B]/18 bg-[#1F130E] p-6 transition-colors hover:border-[#C9A24B]/40"
            >
              <div>
                <p className="font-display text-xl text-[#EFE3CC]">{a.tier_name}</p>
                {a.delivery_method ? (
                  <>
                    <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs tracking-[0.16em] uppercase text-[#B7A488]">
                      <span className="flex items-center gap-2">
                        <MessageCircle className="w-3.5 h-3.5 text-[#C9A24B]" strokeWidth={1.6} /> {a.delivery_method}
                      </span>
                    </div>
                    {a.questions && a.questions.length > 0 ? (
                      <ul className="mt-3 space-y-1 text-sm text-[#8D7C63]">
                        {a.questions.map((q, qi) => (
                          <li key={qi} className="italic">"{q}"</li>
                        ))}
                      </ul>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs tracking-[0.16em] uppercase text-[#B7A488]">
                      <span className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-[#C9A24B]" strokeWidth={1.6} /> {a.date}
                      </span>
                      <span className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-[#C9A24B]" strokeWidth={1.6} /> {a.time}
                      </span>
                    </div>
                    {a.note ? (
                      <p className="mt-3 text-sm text-[#8D7C63] italic max-w-xl">"{a.note}"</p>
                    ) : null}
                  </>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#C9A24B] border border-[#C9A24B]/30 rounded-full px-3 py-1">
                  {a.status || "scheduled"}
                </span>
                {a.delivery_method ? null : (
                  <Link
                    to={`/reschedule?session_id=${a.session_id}`}
                    data-testid="account-reschedule-link"
                    className="text-xs tracking-[0.16em] uppercase text-[#B7A488] hover:text-[#E9D3A0] transition-colors underline underline-offset-4 decoration-[#C9A24B]/40"
                  >
                    Reschedule
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function GoogleGlyph() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
